import { fetchEventSource, } from '@microsoft/fetch-event-source';
import  type { EventSourceMessage,  FetchEventSourceInit } from '@microsoft/fetch-event-source';
import _ from 'lodash';
import { SSEResponse, SSEStreamService, StartOptions } from './sse-stream-service.interface';
import { BusinessError, FatalError } from './error';

declare global {
    type Nullable<T> = T | null;
}

interface SSEStreamHeadersOptions {
    asyncToken: () => Promise<string>,
    lang: string,
    from?: string
}

interface SSEStreamOptions {
    data?: unknown;
    url: string;
    headerOptions: SSEStreamHeadersOptions;
    refreshToken: () => Promise<void>;
    fetchOptions?: FetchEventSourceInit
}

let cachedSSEToken: string | null = null;

/**
 *
 * @param useCachedToken cache token
 * @param asyncTokenFn callback to get token
 * @returns  SSE token
 */
function getSSEToken(useCachedToken = true, asyncTokenFn: () => Promise<string>): Promise<string> {
    if (useCachedToken && cachedSSEToken) return Promise.resolve(cachedSSEToken);
   return asyncTokenFn()
        .then(token => {
            cachedSSEToken = token;
            return token;
        }).catch(() => {
            return '';
        })
}

const UNAUTHORIZED = 401;
const BAD_REQUEST = 400;
const INTERNAL_SERVER_ERROR = 500;

export class SSEStreamServiceImpl<T> implements SSEStreamService<T> {
    private _url: string;
    private _data?: unknown;
    private _headerOptions: SSEStreamHeadersOptions;
    private _refreshToken: (data?: unknown) => Promise<void>;
    private _fetchOptions?: FetchEventSourceInit;

    private _onopenHandler: Nullable<(response: Response) => void> = null;
    private _onmessageHandler: Nullable<(data: SSEResponse<T>) => void> = null;
    private _onerrorHandler: Nullable<(err: any) => void> = null

    private _oncloseHandler: Nullable<() => void> = null;
    private _abortController = new AbortController();

    private fetchHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
    };

    constructor(options: SSEStreamOptions) {
        const { data, url, headerOptions, fetchOptions, refreshToken } = options;

        if (_.isEmpty(url)) {
            throw new Error('url is required');
        }

        this._url = url;
        this._data = data;
        this._headerOptions = headerOptions;
        this._refreshToken = refreshToken;
        this._fetchOptions = fetchOptions
    }

    onOpen(callback: (response: Response) => void) {
        this._onopenHandler = callback;
        return this;
    }

    onMessage(callback: (data: SSEResponse<T>) => void) {
        this._onmessageHandler = callback;
        return this;
    }

    onClose(callback: () => void) {
        this._oncloseHandler = callback;
        return this;
    }

    onError(callback: (err: any) => void) {
        this._onerrorHandler = callback;
        return this;
    }

    /**
     *
     * @param customHeaders 自定义 Headers
     */
    public setFetchHeaders(customHeaders?: Record<string, string | null>) {
        const { lang, from } = this._headerOptions;

        const additionalHeaders = {
            'X-Site-Lang': lang.toUpperCase(),
            'X-PatSnap-From': from,
            ...customHeaders
          };

        this.fetchHeaders = _.omitBy({
            ...this.fetchHeaders,
            ...additionalHeaders
          }, _.isNil) as Record<string, string>

          return this
    }

    /**
     * 关闭 SSE 连接
     */
    public close() {
        this._abortController.abort();
        console.log('destroy: ', new Date());
    }

    /**
     * 重新连接 SSE
     */
    private _restartEventSource() {
        this.close();
        this._refreshToken({
            url: this._url,
            method: this._fetchOptions?.method?.toLocaleLowerCase() || 'post',
            data: JSON.stringify(this._data),
            headers: {
                'Content-Type': 'application/json',
            }
        }).then(() => {
            this.start({
                useCachedToken: false,
                retryToken: false
            });
        }).catch((err: any) => {
            this._onerrorHandler?.(err);
        });
    }


    private _handleMessageEvent(msg: EventSourceMessage) {
        let jsonData: SSEResponse<T> | undefined;
        try {
            jsonData = JSON.parse(msg.data) as SSEResponse<T>;
        } catch (error) {
            throw new FatalError('Failed to parse response from server');
        }
        const { status } = jsonData;
        if (status) {
            this._onmessageHandler?.(jsonData);
        } else {
            throw new BusinessError({
                message: jsonData.error_message,
                error_code: jsonData.error_code,
                response: jsonData,
            });
        }
    }

    /**
     *
     * @param startOptions 是否使用缓存的 token
     * @returns SSE 连接Promise
     */
    start(startOptions: StartOptions = { useCachedToken: true, retryToken: true } ) {
        const { asyncToken } = this._headerOptions;
        const { useCachedToken, retryToken } = startOptions
        return getSSEToken(useCachedToken, asyncToken).then(token => {

            this.setFetchHeaders({
                Authorization: token ? `Bearer ${token}` : null
            })

            const fetchSSEOptions: FetchEventSourceInit = {
                method: 'POST',
                openWhenHidden: true,
                headers: this.fetchHeaders,
                signal: this._abortController.signal,
                body: JSON.stringify(this._data),
                onopen: async (response: Response) => {
                    if (response.ok) {
                        this._onopenHandler?.(response);
                    } else if (response.status === UNAUTHORIZED && retryToken) {
                        this._restartEventSource();
                    } else if (response.status >= BAD_REQUEST  && response.status <= INTERNAL_SERVER_ERROR) {
                        throw new FatalError(response.statusText);
                    }
                },
                onmessage: (msg: EventSourceMessage) => this._handleMessageEvent(msg),
                onclose: () => {
                    this._oncloseHandler?.();
                    this._abortController.abort();
                },
                onerror: (err: any) => {
                    this._onerrorHandler?.(err);
                    this._abortController.abort();
                    throw err;
                },
                ...this._fetchOptions
            };

            return fetchEventSource(this._url, fetchSSEOptions);
        })
    }
}
